import React from 'react';
import {
	useReactTable,
	getCoreRowModel,
	getPaginationRowModel,
	getFilteredRowModel,
	getSortedRowModel,
	flexRender
} from '@tanstack/react-table';

import { Button, Form, Table } from 'react-bootstrap';
import TableToolbar from './TableToolbar';
import LoadingSpinner from '../LoadingSpinner';
import ErrorMessage from '../ErrorMessage';

import { isNotNullOrUndefined } from '../../utils/objectUtils';
import { defaultPropGetter } from './PropGetters';
import { DefaultColumnFilter, fuzzyTextFilterFn, NumberRangeColumnFilter, SelectColumnFilter, SliderColumnFilter } from './ColumnFilters';
import { toDisplayText } from '../../utils/renderValue';

const Filter = ({ column, table }) => {
	const { filterVariant } = column.columnDef.meta ?? {}

	switch (filterVariant) {
		case 'SelectColumnFilter':
			return <SelectColumnFilter table={table} column={column} />;
		case 'SliderColumnFilter':
			return <SliderColumnFilter table={table} column={column} />;
		case 'NumberRangeColumnFilter':
			return <NumberRangeColumnFilter table={table} column={column} />;
		default:
			return <DefaultColumnFilter table={table} column={column} />;
	}
};

const TableEditable = ({
	columns,
	data,
	updateMyData,
	skipReset,
	singularEntityName,
	pluralEntityName,
	addEntityModal,
	deleteEntityModal,
	importButton,
	exportButton,
	otherButtons,
	striped = true,
	bordered = true,
	loading = false,
	error = null,
	showToolbar = true,
	getHeaderProps = defaultPropGetter,
	getColumnProps = defaultPropGetter,
	getRowProps = defaultPropGetter,
	getCellProps = defaultPropGetter,
	setFilteredData,
}) => {
	/**
	 * TanStack Table v8 is "headless". You configure all features in
	 * a single `useReactTable()` call, and it returns a `table` object
	 * with many utilities (header groups, row models, states, etc.).
	 */

	// -- Define custom filter types  -------------------------------------------
	const filterFns = React.useMemo(
		() => ({
			// This replaces your "fuzzyTextFilterFn"
			fuzzy: fuzzyTextFilterFn,

			// Example custom "text" filter (startsWith). You can name it what you like:
			startsWith: (row, columnId, filterValue) => {
				const cellValue = row.getValue(columnId);
				if (typeof cellValue !== 'string') return true;
				return cellValue.toLowerCase().startsWith(filterValue.toLowerCase());
			},
		}),
		[]
	);

	// -- Table State (sorting, filtering, pagination, row selection, etc.) ----

	// Global filter (i.e. the "search" box you had)
	const [globalFilter, setGlobalFilter] = React.useState('');

	// Column-level filters
	const [columnFilters, setColumnFilters] = React.useState([]);

	// Sorting
	const [sorting, setSorting] = React.useState([]);

	// Pagination
	const [pagination, setPagination] = React.useState({
		pageIndex: 0,
		pageSize: 50, // default page size
	});

	// If you want row selection enabled:
	const [rowSelection, setRowSelection] = React.useState({});

	// Build the initialVisibility object
	const initialVisibility = React.useMemo(() => {
		const visibility = {};
		columns.forEach(col => {
			// If meta.show is explicitly true/false, store that in the visibility
			if (typeof col.meta?.show === 'boolean') {
				visibility[col.id] = col.meta.show;
			}
		});
		return visibility;
	}, [columns]);

	const defaultColumn = React.useMemo(
		() => ({
			cell: ({ getValue }) => toDisplayText(getValue()),
		}),
		[]
	);

	// -- Instantiate your table -----------------------------------------------
	const table = useReactTable({
		data,
		columns,
		defaultColumn,
		filterFns,

		initialState: {
			columnVisibility: initialVisibility,
		},

		// This "initial state" is somewhat analogous to `initialState` in v7,
		// but for v8 you typically pass them into the `state` object or set them
		// after the table is created (depending on your preference).
		state: {
			// Attach any or all states you want to control
			globalFilter,
			columnFilters,
			sorting,
			pagination,
			rowSelection, // if needed
		},

		// Called when the table wants to update global filter
		onGlobalFilterChange: setGlobalFilter,

		// Called when table wants to update column-level filters
		onColumnFiltersChange: setColumnFilters,

		// Called when table wants to update sorting
		onSortingChange: setSorting,

		// Called when table wants to update pagination
		onPaginationChange: setPagination,

		// Called when table wants to update row selection
		onRowSelectionChange: setRowSelection,

		// If you want columns to start hidden:
		// You can dynamically hide columns by setting them in an effect or so.
		// e.g., after the table is created, call table.setColumnVisibility({ colId: false, ... })

		// Let’s replicate your `skipReset` logic:
		// By default, TanStack auto-resets certain states; you can override:
		autoResetAll: !skipReset,

		// Provide whichever "row models" you need:
		getCoreRowModel: getCoreRowModel(),

		// For filtering:
		getFilteredRowModel: getFilteredRowModel(),

		// For sorting:
		getSortedRowModel: getSortedRowModel(),

		// For pagination:
		getPaginationRowModel: getPaginationRowModel(),

		// If you want row selection, you can also add:
		getRowId: (row) => row.id, // or your own unique ID
		enableRowSelection: true,

		// Our custom function to update data from editable cells, 
		// just like your v7 “updateMyData” usage:
		meta: {
			updateMyData,
		},
	});

	// Just like v7’s `page`, but in v8 it’s `table.getPaginationRowModel().rows`.
	const currentPageRows = table.getPaginationRowModel().rows;

	// If you want the total page count:
	const pageCount = table.getPageCount();

	// The “pageIndex” & “pageSize” come from `table.getState().pagination`
	const {
		pageIndex,
		pageSize,
	} = table.getState().pagination;

	// For "go to page," you can call table.setPageIndex(newIndex).
	// For next/previous, table.nextPage() or table.previousPage(), etc.
	const canPreviousPage = pageIndex > 0;
	const canNextPage = pageIndex < pageCount - 1;

	// If you want to pass the final “page” of rows to something else:
	React.useEffect(() => {
		if (isNotNullOrUndefined(setFilteredData)) {
			setFilteredData(currentPageRows);
		}
	}, [currentPageRows, setFilteredData]);

	// ------------------------------------------------------------------------
	// Render your UI
	return (
		<>
			<TableToolbar
				showToolbar={showToolbar}
				// If row selection is enabled:
				// numSelected={Object.keys(rowSelection).length}
				// Otherwise, keep your old logic:
				numSelected={0}
				singularEntityName={singularEntityName}
				pluralEntityName={pluralEntityName}
				addEntityModal={addEntityModal}
				deleteEntityModal={deleteEntityModal}
				preGlobalFilteredRows={
					// If you want to replicate "preGlobalFilteredRows" from v7,
					// you can just pass table.getCoreRowModel().rows, or data.length, etc.
					table.getCoreRowModel().rows
				}
				setGlobalFilter={setGlobalFilter}
				globalFilter={globalFilter}
				data={data}
				importButton={importButton}
				exportButton={exportButton}
				otherButtons={otherButtons}
				table={table}
			/>

			{isNotNullOrUndefined(error) ? (
				<ErrorMessage error={error} />
			) : loading ? (
				<LoadingSpinner loading={loading} />
			) : (
				<div
					className="d-block mw-100"
					style={{ overflowX: 'auto', overflowY: 'auto', height: 500, position: 'relative' }}
				>
					<Table
						striped={striped}
						bordered={bordered}
						className="table-editable text-nowrap mb-0 table-centered table-sm"
					// You can still spread any table props if you want (not required in v8)
					>
						<thead style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
							{table.getHeaderGroups().map(headerGroup => (
								<tr key={headerGroup.id}>
									{headerGroup.headers.map(header => {
										// Build custom props as needed
										const colProps = {
											className: header.column.columnDef.className,
											style: header.column.columnDef.style,
											...getColumnProps(header.column),
											...getHeaderProps(header.column),
										};

										return (
											<th key={header.id} {...colProps}>
												<div
													// For sorting, TanStack offers header.column.getToggleSortingHandler():
													// That returns an onClick handler that cycles through sort directions.
													onClick={header.column.getToggleSortingHandler()}
													style={{ cursor: 'pointer', userSelect: 'none' }}
												>
													{flexRender(
														header.column.columnDef.header,
														header.getContext()
													)}
													{/* Sort indicator */}
													{{
														asc: ' 🔼',
														desc: ' 🔽',
													}[header.column.getIsSorted()] ?? null}
												</div>

												{/* Render a column filter UI if desired: */}
												<div>
													{header.column.getCanFilter() ? (
														<div>
															<Filter column={header.column} table={table} />
														</div>
													) : null}
												</div>
											</th>
										);
									})}
								</tr>
							))}
						</thead>

						<tbody>
							{currentPageRows.map(row => {
								// Build your row props
								const rowProps = {
									...getRowProps(row.original),
									// e.g. style, className, etc.
								};

								return (
									<tr key={row.id} {...rowProps}>
										{row.getVisibleCells().map(cell => {
											const cellProps = {
												className: cell.column.columnDef.className,
												style: cell.column.columnDef.style,
												...getColumnProps(cell.column),
												...getCellProps(cell),
											};
											return (
												<td key={cell.id} {...cellProps}>
													{/*
                            If you had “aggregated” or “placeholder” logic in v7,
                            in v8 it’s typically simpler: just render the cell,
                            or do conditional logic as needed.
                          */}
													{flexRender(
														cell.column.columnDef.cell,
														cell.getContext()
													)}
												</td>
											);
										})}
									</tr>
								);
							})}
						</tbody>
					</Table>
				</div>
			)}

			{/* Pagination controls */}
			<div className="pagination d-flex justify-content-between align-items-center bg-light">
				<div className="d-flex align-items-center">
					<Button
						variant="dark"
						onClick={() => table.setPageIndex(0)}
						disabled={!canPreviousPage}
						className="m-1"
					>
						{'<<'}
					</Button>
					<Button
						variant="dark"
						onClick={() => table.previousPage()}
						disabled={!canPreviousPage}
						className="m-1"
					>
						{'<'}
					</Button>
					<Button
						variant="dark"
						onClick={() => table.nextPage()}
						disabled={!canNextPage}
						className="m-1"
					>
						{'>'}
					</Button>
					<Button
						variant="dark"
						onClick={() => table.setPageIndex(pageCount - 1)}
						disabled={!canNextPage}
						className="m-1"
					>
						{'>>'}
					</Button>
				</div>
				<div className="d-flex align-items-center">
					<span className="m-1">
						Page <strong>{pageIndex + 1} of {pageCount}</strong> | Go to page:
					</span>
					<Form.Control
						type="number"
						defaultValue={pageIndex + 1}
						onChange={(e) => {
							const page = e.target.value ? Number(e.target.value) - 1 : 0;
							table.setPageIndex(page);
						}}
						style={{ width: '100px' }}
					/>
				</div>
				<div className="d-flex align-items-center">
					<Form.Select
						value={pageSize}
						onChange={(e) => {
							table.setPageSize(Number(e.target.value));
						}}
					>
						{[10, 20, 30, 40, 50, 100].map((size) => (
							<option key={size} value={size}>
								Show {size}
							</option>
						))}
					</Form.Select>
				</div>
			</div>
		</>
	);
};

export default TableEditable;
